/* Doing one thing to forty rows.
 *
 * Accounts has had this since the day somebody had to classify eighty rows one
 * drawer at a time. Every other register still worked that way, so this is that
 * bar lifted out whole — the behaviour is Accounts', unchanged, and the reason
 * it is a component rather than a second copy is that the awkward parts are the
 * parts most easily got wrong twice:
 *
 *   One row at a time, with failures collected rather than thrown. A batch that
 *   half-applied and named its half beats an all-or-nothing that leaves you
 *   guessing which forty rows are which.
 *
 *   The selection survives a partial run. Rows that failed stay ticked and
 *   ready to retry; a bar that cleared itself on the way out would take the
 *   only record of what went wrong with it.
 *
 *   Every field says whether the change will stick. Some columns are the
 *   register's own and stand; some are recomputed by the nightly sync and are
 *   gone by morning. A bar that stayed quiet about the difference would offer
 *   changes that look applied and are not.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { Button, Select } from '../ui';
import s from './screens.module.scss';

/** One thing that can be set across a selection. */
export interface BulkField {
  label: string;
  options: { value: string; label: string }[];
  /** Whether a change to this column stays changed. Shown under the bar. */
  note: string;
}

export interface BulkOptions<T> {
  rows: readonly T[];
  idOf: (row: T) => string;
  /** For naming a row in a failure message. */
  nameOf: (row: T) => string;
  fields: Record<string, BulkField>;
  update: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
  remove?: (id: string) => Promise<unknown>;
  /** Query keys to refresh once a run finishes. Readonly all the way down —
   *  qk's keys are `as const` tuples, and asking for mutable ones here would
   *  make every caller spread them into a copy for no reason. */
  invalidate: readonly (readonly unknown[])[];
}

export function useBulk<T>(opts: BulkOptions<T>) {
  const qc = useQueryClient();
  const keys = Object.keys(opts.fields);

  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [field, setField] = useState<string>(keys[0] ?? '');
  const [value, setValue] = useState<string>(opts.fields[keys[0] ?? '']?.options[0]?.value ?? '');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  /* Reset the value with the field, or you carry a status into the depot
     dropdown and apply an account type as a location. */
  const chooseField = (next: string) => {
    setField(next);
    setValue(opts.fields[next]?.options[0]?.value ?? '');
  };

  /** One row at a time, collecting what did not work. */
  const each = async (ids: string[], act: (id: string) => Promise<unknown>) => {
    const failures: string[] = [];
    setFailed([]);
    setProgress({ done: 0, total: ids.length });

    for (const [i, id] of ids.entries()) {
      const row = opts.rows.find((r) => opts.idOf(r) === id);
      try {
        await act(id);
      } catch (err) {
        failures.push(
          `${row ? opts.nameOf(row) : id}: ${err instanceof Error ? err.message : 'failed'}`,
        );
      }
      setProgress({ done: i + 1, total: ids.length });
    }

    setFailed(failures);
    return failures;
  };

  const done = (failures: string[]) => {
    for (const key of opts.invalidate) void qc.invalidateQueries({ queryKey: key });
    setProgress(null);
    // Only on a clean run, so a partial one leaves the failures ticked.
    if (failures.length === 0) setTicked(new Set());
  };

  const apply = useMutation({

    meta: { quiet: true },
    mutationFn: () => each([...ticked], (id) => opts.update(id, { [field]: value })),
    onSuccess: done,
  });

  const del = useMutation({

    meta: { quiet: true },
    mutationFn: () => each([...ticked], (id) => opts.remove!(id)),
    onSuccess: done,
  });

  return {
    ticked,
    setTicked,
    field,
    chooseField,
    value,
    setValue,
    progress,
    failed,
    apply,
    del,
    fields: opts.fields,
  };
}

export function BulkBar<T>({
  bulk,
  onDelete,
  deleteLabel = 'Delete',
  extra,
}: {
  bulk: ReturnType<typeof useBulk<T>>;
  /** Called instead of deleting, so a screen can confirm in its own words.
   *  Return true to go ahead. Omit entirely and no Delete button appears. */
  onDelete?: () => boolean;
  deleteLabel?: string;
  /** Anything else this register wants on the bar. */
  extra?: React.ReactNode;
}) {
  const online = useOnline();
  const { ticked, fields, field, value, progress, failed } = bulk;

  if (ticked.size === 0) return null;

  const busy = bulk.apply.isPending || bulk.del.isPending;

  return (
    <div className={s.bulkBar}>
      <div className={s.bulkCount}>
        <i className="ti ti-checkbox" aria-hidden />
        <b>{ticked.size}</b> selected
      </div>

      {/* One field means no choosing — the select would be a control with a
          single option, which reads as broken rather than simple. */}
      {Object.keys(fields).length > 1 && (
        <Select
          label="Change"
          value={field}
          onChange={bulk.chooseField}
          options={Object.entries(fields).map(([k, v]) => ({ value: k, label: v.label }))}
        />
      )}

      <Select
        label="To"
        value={value}
        onChange={bulk.setValue}
        options={fields[field]?.options ?? []}
      />

      <Button
        primary
        icon={bulk.apply.isPending ? 'ti-loader-2' : 'ti-check'}
        spinning={bulk.apply.isPending}
        disabled={!online || busy || !value}
        onClick={() => bulk.apply.mutate()}
      >
        {progress && bulk.apply.isPending
          ? `Saving ${progress.done} of ${progress.total}…`
          : `Apply to ${ticked.size}`}
      </Button>

      {onDelete && (
        <Button
          icon={bulk.del.isPending ? 'ti-loader-2' : 'ti-trash'}
          spinning={bulk.del.isPending}
          disabled={!online || busy}
          onClick={() => {
            if (onDelete()) bulk.del.mutate();
          }}
        >
          {progress && bulk.del.isPending
            ? `Removing ${progress.done} of ${progress.total}…`
            : deleteLabel}
        </Button>
      )}

      <Button icon="ti-x" onClick={() => bulk.setTicked(new Set())}>
        Clear
      </Button>

      {extra}

      {/* Per field, because the answer genuinely differs between them. */}
      <span className={s.bulkNote}>{fields[field]?.note}</span>

      {failed.length > 0 && (
        <div className={`${s.ownedNote} ${s.bulkFailures}`}>
          <i className="ti ti-alert-triangle" aria-hidden />
          <span>
            {failed.length} of {ticked.size} did not save. Still selected, so they can be tried
            again.
            <br />
            {failed.slice(0, 5).join(' · ')}
            {failed.length > 5 && ` · and ${failed.length - 5} more`}
          </span>
        </div>
      )}
    </div>
  );
}
